import { LogoIcon } from "@/components/common/Logo/LogoIcon"
import { Dialog } from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ArrowRight } from "lucide-react"
import * as React from "react"
import { useOnboarding } from "../context/OnboardingContext"
import { TourId } from "../types/tour.types"

interface TourWelcomeModalProps {
  isOpen: boolean
  tourId: string
  title: string
  description: string
  onClose?: () => void
}

const DialogContentNoClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      )}
    />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
))
DialogContentNoClose.displayName = "DialogContentNoClose"

export const TourWelcomeModal: React.FC<TourWelcomeModalProps> = ({
  isOpen,
  tourId,
  title,
  description,
  onClose,
}) => {
  const { startTour, skipOnboarding } = useOnboarding()
  const { translate } = useTranslation()

  const handleStartTour = () => {
    startTour(tourId as TourId)
  }

  const handleSkipTour = () => {
    skipOnboarding()
    onClose?.()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContentNoClose
        className="border-zinc-700/50 overflow-hidden px-10 py-12 text-center sm:max-w-[480px]"
        style={{
          background:
            "linear-gradient(180deg, rgba(32, 32, 35, 1) 0%, rgba(20, 20, 22, 1) 100%)",
          boxShadow:
            "0 0 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(255, 244, 64, 0.08)",
        }}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <DialogPrimitive.Title className="sr-only">
          {title}
        </DialogPrimitive.Title>

        <div
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255, 244, 64, 0.12) 0%, transparent 55%)",
            filter: "blur(50px)",
          }}
        />

        <div className="relative mb-10 flex items-center justify-center">
          <div
            className="pointer-events-none absolute h-28 w-28"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(255, 244, 64, 0.25) 0%, transparent 65%)",
              filter: "blur(25px)",
            }}
          />
          <LogoIcon className="relative h-11 text-primary" />
        </div>

        <h2 className="mb-3 text-2xl font-semibold tracking-tight text-white">
          {title}
        </h2>

        <p className="text-zinc-300 mb-8 text-base">
          {translate("TOUR/SUBTITLE")}
        </p>

        <p className="text-zinc-500 mx-auto mb-10 max-w-xs text-sm leading-relaxed">
          {description}
        </p>

        <div className="flex items-center gap-3">
          <button
            onClick={handleStartTour}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-6 text-base font-semibold text-black transition-all hover:brightness-110 focus:outline-hidden active:scale-[0.98]"
          >
            {translate("TOUR/START")}
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={handleSkipTour}
            title={translate("TOUR/SKIP_HINT")}
            className="text-zinc-500 hover:text-zinc-400 border-zinc-700/60 hover:border-zinc-600 h-12 rounded-lg border px-5 text-base font-medium opacity-70 transition-all hover:opacity-90 focus:outline-hidden"
          >
            {translate("TOUR/MAYBE_LATER")}
          </button>
        </div>
      </DialogContentNoClose>
    </Dialog>
  )
}
